import React, { useEffect, useState } from 'react';
import type { Venue } from '../../../types/venue';
import { fetchSurveyAnalytics } from '../../../utils/venueApi';
import StarRating from '../shared/StarRating';

interface SurveyResultsPanelProps {
  venue: Venue;
}

interface SurveyAnalytics {
  totalResponses: number;
  questions: {
    questionId: string;
    questionText: string;
    type: string;
    averageRating?: number;
    responseCount: number;
    breakdown?: { answer: string; count: number; percentage: number }[];
    freeTextResponses?: string[];
  }[];
}

const SurveyResultsPanel: React.FC<SurveyResultsPanelProps> = ({ venue }) => {
  const [data, setData] = useState<SurveyAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSurveyAnalytics(venue.id)
      .then((result) => setData(result))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [venue.id]);

  if (!venue.survey?.enabled) {
    return (
      <div className="space-y-4">
        <div>
          <div className="eyebrow mb-1.5">Survey Results</div>
          <h2 className="text-display text-2xl text-coffee-900">Survey results</h2>
        </div>
        <div className="text-center py-16 px-4">
          <div className="w-12 h-12 rounded-xl bg-coffee-50 border border-coffee-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-5 h-5 text-coffee-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="font-semibold text-coffee-900 mb-1">Surveys not enabled</h3>
          <p className="text-sm text-coffee-600 max-w-xs mx-auto">Enable surveys in the onboarding wizard to start collecting guest feedback.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div>
          <div className="eyebrow mb-1.5">Survey Results</div>
          <h2 className="text-display text-2xl text-coffee-900">Survey results</h2>
        </div>
        <div className="animate-pulse space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 rounded-xl bg-coffee-50" />
          ))}
        </div>
      </div>
    );
  }

  if (!data || data.totalResponses === 0) {
    return (
      <div className="space-y-4">
        <div>
          <div className="eyebrow mb-1.5">Survey Results</div>
          <h2 className="text-display text-2xl text-coffee-900">Survey results</h2>
        </div>
        <div className="text-center py-16 px-4">
          <div className="w-12 h-12 rounded-xl bg-coffee-50 border border-coffee-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-5 h-5 text-coffee-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="font-semibold text-coffee-900 mb-1">No responses yet</h3>
          <p className="text-sm text-coffee-600 max-w-xs mx-auto">Responses appear here once guests complete your survey.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="eyebrow mb-1.5">Survey Results</div>
          <h2 className="text-display text-2xl text-coffee-900">Survey results</h2>
        </div>
        <span className="text-sm font-mono text-coffee-500 pt-1">
          {data.totalResponses} response{data.totalResponses !== 1 ? 's' : ''}
        </span>
      </div>

      {data.questions.map((q) => (
        <div key={q.questionId} className="bg-white border border-coffee-100 rounded-2xl p-6">
          <p className="font-semibold text-coffee-900 mb-1">{q.questionText}</p>
          <p className="text-xs font-mono text-coffee-500 mb-4">{q.responseCount} responses</p>

          {q.type === 'star_rating' && q.averageRating != null && (
            <div className="flex items-center gap-3">
              <StarRating value={Math.round(q.averageRating)} readOnly size="sm" />
              <span className="text-xl font-mono font-bold text-coffee-900">
                {q.averageRating.toFixed(1)}
              </span>
            </div>
          )}

          {q.type === 'multiple_choice' && q.breakdown && (
            <div className="space-y-2.5">
              {q.breakdown.map((opt) => (
                <div key={opt.answer}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-coffee-700 font-medium">{opt.answer}</span>
                    <span className="font-mono text-coffee-500">{opt.percentage}%</span>
                  </div>
                  <div className="w-full h-2 bg-coffee-100 rounded-full">
                    <div
                      className="h-2 bg-coffee-600 rounded-full transition-all"
                      style={{ width: `${opt.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {q.type === 'free_text' && q.freeTextResponses && (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {q.freeTextResponses.map((text, i) => (
                <p key={i} className="text-sm text-coffee-700 bg-coffee-50 border border-coffee-100 rounded-xl p-3 leading-relaxed">
                  "{text}"
                </p>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default SurveyResultsPanel;
